// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { FC } from "react";

import {
  CountDedupedTraitConfig,
  InlineAggregatedTrait,
  OrderDedupedTraitConfig,
  ValueType,
  isAggregationTraitConfig,
  isCountDedupedTraitConfig,
  isOrderDedupedTraitConfig,
} from "@hightouch/lib/query/visual/types";
import { Column, Combobox, IconBox, Row, Text } from "@hightouch/htui";

import { MinimalParentModel } from "src/components/audiences/types";
import {
  EventColumn,
  RelationColumn,
} from "src/components/explore/filter-popover/constants";
import { normalizeColumnReferenceIds } from "src/components/explore/filter-popover/utils";
import { INLINE_TRAIT_TYPE_OPTIONS } from "src/components/traits/utils";
import { useFilterableColumnsQuery } from "src/graphql";
import {
  Audience,
  DefaultOperators,
  FilterableColumn,
  InlineAggregatedTraitCondition,
} from "src/types/visual";

import { ConditionTextWrapper } from "./condition-text-wrapper";
import { ErrorMessage } from "./error-message";
import { PropertyInputProps } from "./property-input";
import { PropertyOperatorValueFilter } from "./property-operator-value-filter";
import { TraitInlineSubconditions } from "./trait-filter";
import { getCustomTraitPropertyType } from "./utils";
import { ComboboxFilter } from "./combobox-filter";
import { OnUpdate } from "src/components/explore/utils/condition-builders";

const ColumnWidth = "150px";

type Props = {
  audience: Audience | undefined;
  condition: InlineAggregatedTraitCondition;
  conditionError: string | null | undefined;
  modelId: string | undefined;
  operatorError: string | null | undefined;
  parent: MinimalParentModel;
  propertyError: string | null | undefined;
  loadingSuggestions: boolean;
  suggestions: PropertyInputProps["suggestions"];
  valueError: string | null | undefined;
  isDisabled?: boolean;
  isLoading?: boolean;
  onUpdate: OnUpdate<InlineAggregatedTraitCondition>;
  onSearchUpdate?: (inputValue: string) => void;
};

export const InlineTrait: FC<Readonly<Props>> = ({
  audience,
  condition,
  conditionError,
  modelId,
  operatorError,
  parent,
  propertyError,
  loadingSuggestions,
  suggestions,
  valueError,
  onUpdate,
  onSearchUpdate,
  isDisabled = false,
  isLoading = false,
}) => {
  const relationships = parent.relationships;

  const filterableColumnsQuery = useFilterableColumnsQuery(
    {
      segmentId: modelId?.toString() ?? "",
    },
    {
      enabled: Boolean(modelId),
      select: (data) => data.segments_by_pk?.filterable_audience_columns ?? [],
    },
  );

  const relationshipOptions = parent.relationships
    .filter(
      // Realtime events cannot be used for inline traits
      (relationship) => !relationship.to_model.event?.realtime_event_source_id,
    )
    .map((relationship) => {
      const modelType = relationship.to_model.event
        ? EventColumn
        : RelationColumn;
      const Icon = () => (
        <IconBox color={modelType.shade} size="xs" icon={modelType.icon} />
      );

      return {
        label: relationship.to_model.name || relationship.name,
        value: relationship.id.toString(),
        icon: Icon,
      };
    });

  const relationshipId = condition.property?.column?.relationshipId;
  const relationship = relationships.find(
    ({ id }) => id.toString() === relationshipId?.toString(),
  );
  const relatedModel = relationship?.to_model;

  const filterableColumnReferences = filterableColumnsQuery.data ?? [];

  const filterableColumns = filterableColumnReferences.map(
    (column: FilterableColumn) => ({
      label: column.alias ?? column.name,
      value: normalizeColumnReferenceIds(column.column_reference),
    }),
  );

  const column = condition.property.column;

  const updateRelationshipId = (relationshipId: string) =>
    onUpdate((condition) => {
      condition.property.column.relationshipId = relationshipId;
      condition.property.column.traitConfig = {};
      condition.property.path = [relationshipId];
    });

  const updateTraitType = (traitType: InlineAggregatedTrait["traitType"]) => {
    const propertyType = getCustomTraitPropertyType({
      type: traitType,
      selectedColumn: undefined,
      filterableColumns: filterableColumnReferences,
    });

    onUpdate((condition) => {
      if (condition.value?.type === ValueType.Column) {
        condition.value = undefined;
      }
      condition.property.column.traitType = traitType;
      condition.property.column.traitConfig = {};
      condition.propertyType = propertyType;
      condition.operator = DefaultOperators[propertyType];
    });
  };

  const updateTraitConfig = (
    config: Partial<InlineAggregatedTrait["traitConfig"]>,
  ) => {
    const traitType = condition.property.column.traitType;
    const existingTraitConfig = condition.property.column.traitConfig as
      | OrderDedupedTraitConfig
      | CountDedupedTraitConfig;
    const selectedColumn = isAggregationTraitConfig(traitType, config)
      ? config.column
      : isCountDedupedTraitConfig(traitType, config) ||
          isOrderDedupedTraitConfig(traitType, config)
        ? (config.toSelect ?? existingTraitConfig?.toSelect)
        : undefined;

    const propertyType = getCustomTraitPropertyType({
      type: traitType,
      selectedColumn,
      filterableColumns: filterableColumnReferences.map((col) => ({
        ...col,
        column_reference: normalizeColumnReferenceIds(col.column_reference),
      })),
    });

    onUpdate((condition) => {
      condition.property.column.traitConfig = {
        ...condition.property.column.traitConfig,
        ...config,
      };
      condition.propertyType = propertyType;
      condition.operator = DefaultOperators[propertyType];
    });
  };

  if (!relationshipId) {
    return (
      <Column mt={2}>
        <ComboboxFilter
          isDisabled={isDisabled}
          isInvalid={Boolean(
            propertyError && !condition?.property?.column?.relationshipId,
          )}
          options={relationshipOptions}
          optionAccessories={(option) => ({
            leftAccessory: {
              type: "icon",
              icon: option.icon,
            },
          })}
          placeholder="Select a model..."
          width="2xs"
          value={condition?.property?.column?.relationshipId?.toString()}
          onChange={(value) => {
            if (value) {
              updateRelationshipId(value);
            }
          }}
        />

        {propertyError && <ErrorMessage>{propertyError}</ErrorMessage>}
      </Column>
    );
  }

  return (
    <Column mt={2} gap={2}>
      <Column>
        <Row gap={2} alignItems="flex-start">
          <Column width={ColumnWidth}>
            <Combobox
              isDisabled={isDisabled}
              isInvalid={Boolean(
                propertyError && !condition?.property?.column.traitType,
              )}
              options={INLINE_TRAIT_TYPE_OPTIONS}
              placeholder="Select a trait type"
              value={condition?.property?.column.traitType}
              onChange={(value) => {
                if (value) {
                  updateTraitType(value);
                }
              }}
            />
            {propertyError && !condition?.property?.column.traitType && (
              <ErrorMessage>{propertyError}</ErrorMessage>
            )}
          </Column>

          {/* Average, Count, Sum */}
          {isAggregationTraitConfig(column.traitType, column.traitConfig) && (
            <Column width={ColumnWidth}>
              <Combobox
                isDisabled={isDisabled}
                isInvalid={Boolean(propertyError && !column.traitConfig.column)}
                isLoading={filterableColumnsQuery.isLoading}
                options={filterableColumns}
                placeholder="Select a column"
                value={normalizeColumnReferenceIds(column.traitConfig.column)}
                popoverWidth="xs"
                onChange={(value) => {
                  if (value) {
                    updateTraitConfig({ column: value });
                  }
                }}
              />
              {propertyError && !column.traitConfig.column && (
                <ErrorMessage>{propertyError}</ErrorMessage>
              )}
            </Column>
          )}

          {/* Least Frequent, Most Frequent */}
          {isCountDedupedTraitConfig(column.traitType, column.traitConfig) && (
            <Column width={ColumnWidth}>
              <Combobox
                isDisabled={isDisabled}
                isInvalid={Boolean(
                  propertyError && !column.traitConfig.toSelect,
                )}
                isLoading={filterableColumnsQuery.isLoading}
                options={filterableColumns}
                placeholder="Select a column"
                popoverWidth="xs"
                value={normalizeColumnReferenceIds(column.traitConfig.toSelect)}
                onChange={(value) => {
                  if (value) {
                    updateTraitConfig({ toSelect: value });
                  }
                }}
              />
              {propertyError && !column.traitConfig.toSelect && (
                <ErrorMessage>{propertyError}</ErrorMessage>
              )}
            </Column>
          )}

          {/* First, Last */}
          {isOrderDedupedTraitConfig(column.traitType, column.traitConfig) && (
            <Row gap={2}>
              <Column width={ColumnWidth}>
                <Combobox
                  isDisabled={isDisabled}
                  isInvalid={Boolean(
                    propertyError && !column.traitConfig.toSelect,
                  )}
                  isLoading={filterableColumnsQuery.isLoading}
                  options={filterableColumns}
                  placeholder="Select a column"
                  popoverWidth="xs"
                  value={normalizeColumnReferenceIds(
                    column.traitConfig.toSelect,
                  )}
                  onChange={(value) => {
                    if (value) {
                      updateTraitConfig({ toSelect: value });
                    }
                  }}
                />
                {propertyError && !column.traitConfig.toSelect && (
                  <ErrorMessage>{propertyError}</ErrorMessage>
                )}
              </Column>

              <ConditionTextWrapper>
                <Text color="text.secondary">ordered by</Text>
              </ConditionTextWrapper>

              <Column width={ColumnWidth}>
                <Combobox
                  isDisabled={isDisabled}
                  isInvalid={Boolean(
                    propertyError && !column.traitConfig.orderBy,
                  )}
                  isLoading={filterableColumnsQuery.isLoading}
                  options={filterableColumns}
                  placeholder="Select a column"
                  popoverWidth="xs"
                  value={normalizeColumnReferenceIds(
                    column.traitConfig.orderBy,
                  )}
                  onChange={(value) => {
                    if (value) {
                      updateTraitConfig({ orderBy: value });
                    }
                  }}
                />
                {propertyError && !column.traitConfig.orderBy && (
                  <ErrorMessage>{propertyError}</ErrorMessage>
                )}
              </Column>
            </Row>
          )}

          <ConditionTextWrapper>
            <Text color="text.secondary">
              of {relatedModel?.event ? "events" : "rows"} in
            </Text>
          </ConditionTextWrapper>

          <Column>
            <Combobox
              isDisabled={isDisabled}
              isInvalid={Boolean(
                propertyError && !condition?.property?.column?.relationshipId,
              )}
              options={relationshipOptions}
              optionAccessories={(option) => ({
                leftAccessory: {
                  type: "icon",
                  icon: option.icon,
                },
              })}
              placeholder="Select a model"
              width="3xs"
              value={condition?.property?.column?.relationshipId?.toString()}
              onChange={(value) => {
                if (value) {
                  updateRelationshipId(value);
                }
              }}
            />
            {propertyError && !condition?.property?.column?.relationshipId && (
              <ErrorMessage>{propertyError}</ErrorMessage>
            )}
          </Column>

          <PropertyOperatorValueFilter
            isDisabled={isDisabled}
            condition={condition}
            loadingSuggestions={loadingSuggestions}
            operatorError={operatorError}
            parent={parent}
            suggestions={suggestions}
            valueError={valueError}
            onUpdate={onUpdate}
            onSearchUpdate={onSearchUpdate}
          />
        </Row>
      </Column>

      <Row mt={2} pl={10}>
        <TraitInlineSubconditions
          isLoading={isLoading}
          isDisabled={isDisabled}
          audience={audience}
          condition={condition}
          parent={parent}
          onUpdate={onUpdate}
        />
      </Row>

      {conditionError && <ErrorMessage>{conditionError}</ErrorMessage>}
    </Column>
  );
};
